import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Briefcase, GraduationCap } from "lucide-react";
import axios from "axios";
import SEOHead, { personSchema } from "../components/SEOHead";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;
const FALLBACK_PORTRAIT = "/api/files/niharika-about-portrait.jpg";

const SC_DEFAULTS = {
  about_bio_intro: "~10 years across Amazon, Nestle, Zepto, Dabur and Emami. What I bring is a combination rarely found in one profile: the analytical rigour of a data-driven performance marketer, the brand sensibility of a seasoned strategist, and the operational velocity of someone who's actually built things at scale.",
  about_bio_story: "I started my career as a project engineer at Wipro. Most of my early thinking was shaped by systems. How things are built, how they scale, and where they tend to break.\n\nI moved into marketing through an MBA at XIMB. It wasn’t a very planned shift. I just found myself getting more interested in how decisions get made on the consumer side, not just how systems behave.\n\nAt Emami and Dabur, I got a first-hand view of how growth actually plays out in the real world. A lot of it is more constrained than it looks from the outside. Distribution, pricing, internal trade-offs, and the constant balance between brand building and short-term results. It made me question how much of what we call strategy really holds up once execution begins.\n\nNestlé was where things became more complete.\n\nI was working across brand strategy, consumer research, communication, and go-to-market decisions, and seeing how all of that translates into actual business outcomes, especially across digital and marketplaces. I also got to work on digital-first launches and think more closely about ROI across the mix. That’s where brand and performance stopped feeling like separate conversations and started feeling like the same problem viewed from different angles.\n\nAmazon changed the scale of that thinking.\n\nManaging large budgets and revenue meant being more disciplined about measurement, attribution, and trade-offs. I was also working closely with a wide range of D2C brands, which added a very different perspective on how growth is built when you don’t have the advantages of a large organisation. It made it clearer how often metrics only tell part of the story, and how much context sits behind them.\n\nAt Zepto, the role cut across category, events, performance, CRM, and customer lifecycle.\n\nThe work was less about any one piece and more about how everything comes together. Looking at growth as one flow, from demand to conversion to repeat behaviour, instead of separate channel problems.\n\nMost of the effort went into tightening those connections. Making sure what we were pushing matched what the product and category could support, and that lifecycle was building on top of acquisition rather than running separately from it.\n\nA lot of the gains came from getting these pieces to work better together. It wasn’t usually a lack of effort. More that things just didn’t line up fully.\n\nThis site comes out of that.\n\nMostly a way for me to think through some of these things more clearly. What actually drives growth, what tends to get overstated, and where things don’t hold up as well as they seem.",
  about_achievements: [
    { metric: "~20%", label: "MoM growth driven at Zepto" },
    { metric: "₹40Cr+", label: "Monthly budget owned at Amazon" },
    { metric: "2x", label: "Category growth delivered" },
    { metric: "43%", label: "ROI improvement in FMCG" },
  ],
  experience: [
    { role: "Startup Advisor", company: "Brand, Growth & AI-Led Marketing", period: "Jan 2026 – Present", highlight: true },
    { role: "Senior Director – Category Growth & Marketing", company: "Zepto", period: "Oct 2024 – Sep 2025", highlight: false },
    { role: "Senior Category Marketing Manager, Apparel", company: "Amazon India", period: "Jul 2022 – Oct 2024", highlight: false },
    { role: "Brand Manager", company: "Nestlé India (MAGGI Flavor World)", period: "May 2021 – Jul 2022", highlight: false },
    { role: "Product Manager", company: "Dabur India Ltd.", period: "Aug 2020 – May 2021", highlight: false },
    { role: "Assistant Brand Manager", company: "Emami Ltd.", period: "Jun 2017 – Aug 2020", highlight: false },
  ],
  skills: [
    "Brand Strategy & Storytelling", "Omnichannel GTM Strategy", "Performance Marketing (Meta, Google)",
    "CRM & Customer Lifecycle", "P&L Management", "AI in Marketing",
    "D2C & Marketplace Scale", "Media Mix Modeling", "Growth Loops",
    "CAC & CLTV Optimization", "Content Strategy", "Category Marketing",
  ],
  education: [
    { degree: "MBA — Marketing", school: "XIMB (Xavier Institute of Management)" },
  ],
  award_title: "Customer Obsession Award",
  award_org: "Amazon India",
};

export default function AboutPage() {
  const [aboutMeta, setAboutMeta] = useState({ profile_photo: null, tagline: "" });
  const [sc, setSc] = useState(SC_DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/public/about-content`).then((r) => setAboutMeta(r.data)).catch(() => {});
    axios.get(`${API}/public/site-content`).then((r) => setSc({ ...SC_DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  const photoSrc = aboutMeta.profile_photo
    ? `${BACKEND_URL}${aboutMeta.profile_photo}`
    : FALLBACK_PORTRAIT;

  const tagline = aboutMeta.tagline || "Building brands that consumers trust, teams that win, and strategies that scale.";
  const experience = sc.experience || SC_DEFAULTS.experience;
  const skills = sc.skills || SC_DEFAULTS.skills;
  const achievements = sc.about_achievements || SC_DEFAULTS.about_achievements;
  const education = sc.education || SC_DEFAULTS.education;
  const bioStoryParagraphs = (sc.about_bio_story || SC_DEFAULTS.about_bio_story).split("\n\n").filter(Boolean);

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title="About — Niharika Patel"
        description="~10 years across Amazon, Nestle, Zepto, Dabur and Emami. Growth & Marketing Consultant helping B2C startups build integrated growth systems. Brand, performance, CRM."
        url={`${BACKEND_URL}/about`}
        schema={personSchema}
      />

      {/* Hero */}
      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 sm:py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">About</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-6">
              Niharika<br /><span className="font-semibold">Patel</span>
            </h1>
            <blockquote className="border-l-4 border-[#002FA7] pl-5 text-lg sm:text-xl text-[#0A0A0A] font-['Outfit'] italic leading-relaxed mb-6">
              "{tagline}"
            </blockquote>
            <p className="text-[#4B5563] leading-relaxed text-base sm:text-lg mb-8 max-w-lg">
              {sc.about_bio_intro || SC_DEFAULTS.about_bio_intro}
            </p>
            <Link to="/book" className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#001D66] transition-colors">
              Work With Me <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative max-w-sm w-full">
              <div className="absolute -bottom-3 right-0 sm:-bottom-4 sm:-right-4 w-full h-full border border-[#E5E7EB]" />
              <img src={photoSrc} alt="Niharika Patel" className="w-full object-cover aspect-[3/4] relative z-10" data-testid="about-portrait" />
            </div>
          </div>
        </div>
      </div>

      {/* Key Numbers */}
      <div className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {achievements.map((a, i) => (
            <div key={i} className="text-center border border-[#E5E7EB] bg-white p-6">
              <p className="font-['Outfit'] text-3xl font-bold text-[#002FA7] mb-1">{a.metric}</p>
              <p className="text-xs text-[#6B7280] leading-relaxed">{a.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The Story */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">The Story</p>
          <div className="space-y-4 text-[#4B5563] leading-relaxed">
            {bioStoryParagraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>

        {/* Skills */}
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">Skills</p>
          <div className="flex flex-wrap gap-2">
            {skills.map((s, i) => (
              <span key={i} className="text-xs border border-[#E5E7EB] px-3 py-1.5 text-[#4B5563] hover:border-[#002FA7] hover:text-[#002FA7] transition-colors">
                {s}
              </span>
            ))}
          </div>

          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4 mt-8">Education</p>
          <div className="space-y-3">
            {education.map((e, i) => (
              <div key={i} className="flex gap-3 items-start">
                <GraduationCap size={16} className={`${i === 0 ? "text-[#002FA7]" : "text-[#6B7280]"} mt-0.5 flex-shrink-0`} />
                <div>
                  <p className="text-sm font-semibold text-[#0A0A0A]">{e.degree}</p>
                  <p className="text-xs text-[#6B7280]">{e.school}</p>
                </div>
              </div>
            ))}
          </div>

          {(sc.award_title || SC_DEFAULTS.award_title) && (
            <div className="mt-8 border border-[#E5E7EB] p-4">
              <Award size={16} className="text-[#002FA7] mb-2" />
              <p className="text-sm font-semibold text-[#0A0A0A]">{sc.award_title || SC_DEFAULTS.award_title}</p>
              <p className="text-xs text-[#6B7280]">{sc.award_org || SC_DEFAULTS.award_org}</p>
            </div>
          )}
        </div>
      </div>

      {/* Experience Timeline */}
      <div className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="flex items-center gap-2 mb-8">
            <Briefcase size={16} className="text-[#002FA7]" />
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7]">Experience</p>
          </div>
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-[#E5E7EB] ml-[7px]" />
            <div className="space-y-6">
              {experience.map((e, i) => (
                <div key={i} className="flex gap-4 sm:gap-6 pl-7 sm:pl-8 relative">
                  <div className={`absolute left-0 top-2 w-3.5 h-3.5 border-2 flex-shrink-0 ${e.highlight ? "bg-[#002FA7] border-[#002FA7]" : "bg-white border-[#D1D5DB]"}`} />
                  <div className={`border border-[#E5E7EB] p-4 flex-1 bg-white ${e.highlight ? "border-l-2 border-l-[#002FA7]" : ""}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                      <p className="font-['Outfit'] font-semibold text-[#0A0A0A]">{e.role}</p>
                      <p className="text-xs text-[#9CA3AF] sm:whitespace-nowrap">{e.period}</p>
                    </div>
                    <p className="text-sm text-[#6B7280]">{e.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="border-t border-[#E5E7EB] bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 text-center">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-medium text-white mb-4">Ready to build your growth system?</h2>
          <p className="text-white/70 mb-8 max-w-md mx-auto">Tell me about your startup. I respond personally within 48 hours.</p>
          <Link to="/book" className="inline-flex items-center gap-2 bg-white text-[#002FA7] px-7 py-3.5 font-semibold hover:bg-white/90 transition-colors text-sm">
            Work With Me <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
